"use client";

import React, { useEffect, useState } from "react";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { fetchHomePageMinimalData, HomePageMinimalSectionShell } from "@/api-service/homePage";
import LazySectionShell from "@/components/homepage/LazySectionShell";

export interface ProductNotFoundProps {
  type?: "product" | "brand" | "collection" | "search";
  title?: string;
  message?: string;
  keyword?: string;
  actionText?: string;
  onAction?: () => void;
  showCode?: boolean;
  showDynamicShowcase?: boolean;
}

export default function ProductNotFound({
  type = "product",
  title,
  message,
  keyword,
  actionText,
  onAction,
  showCode = true,
  showDynamicShowcase = true,
}: ProductNotFoundProps) {
  const { fastPush } = useFastRouter();
  const [categoryShell, setCategoryShell] = useState<HomePageMinimalSectionShell | null>(null);
  const [brandShell, setBrandShell] = useState<HomePageMinimalSectionShell | null>(null);

  useEffect(() => {
    if (!showDynamicShowcase) return;
    let isMounted = true;
    const loadSections = async () => {
      try {
        const res = await fetchHomePageMinimalData();
        const sections = res?.data?.sections || [];
        if (isMounted && sections.length > 0) {
          const firstCat = sections.find(
            (s) => (s.sectionType || "").toUpperCase() === "CATEGORIES"
          );
          const firstBrand = sections.find(
            (s) => (s.sectionType || "").toUpperCase() === "BRANDS"
          );
          if (firstCat) setCategoryShell(firstCat);
          if (firstBrand) setBrandShell(firstBrand);
        }
      } catch (err) {
        console.error("Failed to load home page sections for Not Found page:", err);
      }
    };

    loadSections();
    return () => {
      isMounted = false;
    };
  }, [showDynamicShowcase]);

  // Dynamic Titles and Messages
  const getTitle = () => {
    if (title) return title;
    switch (type) {
      case "brand":
        return "Brand Not Found";
      case "collection":
        return "Collection Empty";
      case "search":
        return keyword ? `No Results for "${decodeURIComponent(keyword)}"` : "No Results Found";
      case "product":
      default:
        return "Product Not Found";
    }
  };

  const getMessage = () => {
    if (message) return message;
    switch (type) {
      case "brand":
        return "Sorry, the brand you are looking for does not exist, has been removed, or has no products.";
      case "collection":
        return "Sorry, there are currently no products available in this collection.";
      case "search":
        return "Sorry, we couldn't find any products matching your search query. Please try again with different keywords.";
      case "product":
      default:
        return "Sorry, the page or product you are looking for does not exist or has been moved.";
    }
  };

  const handlePrimaryAction = () => {
    if (onAction) {
      onAction();
    } else {
      fastPush("/");
    }
  };

  return (
    <div className="w-full py-12 md:py-20 px-4 flex flex-col items-center justify-center text-center">
      {/* Main 404 Hero Container */}
      <div className="max-w-lg w-full flex flex-col items-center space-y-4">
        
        {/* Large 404 Header Code */}
        {showCode && (
          <h1 className="text-6xl sm:text-7xl font-black text-slate-900 tracking-tight leading-none">
            404
          </h1>
        )}

        {/* Heading */}
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight leading-snug">
          {getTitle()}
        </h2>

        {/* Subtitle message */}
        <p className="text-xs sm:text-sm text-slate-500 font-medium leading-relaxed max-w-md mx-auto">
          {getMessage()}
        </p>

        {/* Primary Action Button */}
        <div className="pt-2">
          <button
            type="button"
            onClick={handlePrimaryAction}
            className="inline-flex items-center justify-center bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white text-xs sm:text-sm font-bold px-8 py-3 rounded-xl shadow-xs transition-colors cursor-pointer"
          >
            {actionText || "Return Home"}
          </button>
        </div>

      </div>

      {/* Dynamic Category & Brand Showcase Sections */}
      {showDynamicShowcase && (categoryShell || brandShell) && (
        <div className="w-full max-w-7xl mx-auto mt-14 pt-10 border-t border-slate-200/80 text-left space-y-12">
          {categoryShell && (
            <LazySectionShell shell={categoryShell} isFirst={true} />
          )}
          {brandShell && (
            <LazySectionShell shell={brandShell} isFirst={true} />
          )}
        </div>
      )}
    </div>
  );
}
